export const AppStyles = {
  // ===== COLORS =====
  colors: {
    // Primary Brand Colors
    primary: {
      main: '#BCB976',        // Light olive green
      light: '#7BB3F0',       // Light blue, lighter shade for hover states
      dark: "#918f5a",        // Dark olive green
      contrastText: '#FFFFFF', // Text color on primary backgrounds
    },
    
    // Secondary Colors
    secondary: {
      main: '#F5A623',        // Golden accent color
      light: '#F7C55C',       // Lighter golden shade
      dark: '#D68910',        // Darker golden shade
      contrastText: '#FFFFFF',
    },
    
    // Success/Positive Colors
    success: {
      main: '#4CAF50',        // Green for success states
      light: '#81C784',       // Light green
      dark: '#388E3C',        // Dark green
      contrastText: '#FFFFFF',
    },
    
    // Warning Colors
    warning: {
      main: '#FF9800',        // Orange for warnings
      light: '#FFB74D',       // Light orange
      dark: '#F57C00',        // Dark orange
      contrastText: '#FFFFFF',
    },
    
    // Error/Danger Colors
    error: {
      main: '#F44336',        // Red for errors
      light: '#E57373',       // Light red
      dark: '#D32F2F',        // Dark red
      contrastText: '#FFFFFF',
    },
    
    // Info Colors
    info: {
      main: '#2196F3',        // Blue for info
      light: '#64B5F6',       // Light blue
      dark: '#1976D2',        // Dark blue
      contrastText: '#FFFFFF',
    },
    
    // Neutral Colors
    neutral: {
      white: '#FFFFFF',
      black: '#000000',
      gray50: '#FAFAFA',
      gray100: '#F5F5F5',
      gray200: '#EEEEEE',
      gray300: '#E0E0E0',
      gray400: '#BDBDBD',
      gray500: '#9E9E9E',
      gray600: '#757575',
      gray700: '#616161',
      gray800: '#424242',
      gray900: '#212121',
    },
    
    // Background Colors
    background: {
      default: '#FAFAFA',     // Main app background
      paper: '#FFFFFF',       // Card/paper backgrounds
      secondary: '#F5F5F5',   // Secondary background
      tertiary: '#EEEEEE',    // Tertiary background
    },
    
    // Text Colors
    text: {
      primary: '#212121',     // Main text color
      secondary: '#757575',   // Secondary text
      disabled: '#BDBDBD',    // Disabled text
      hint: '#9E9E9E',        // Hint text
    },
    
    // Border Colors
    border: {
      light: '#E0E0E0',
      medium: '#BDBDBD',
      dark: '#9E9E9E',
    },
    
    // Status Colors
    status: {
      healthy: '#4CAF50',     // Green for healthy pets
      sick: '#FF9800',        // Orange for sick pets
      critical: '#F44336',    // Red for critical conditions
      recovering: '#2196F3',  // Blue for recovering pets
    },
  },

  // ===== TYPOGRAPHY =====
  typography: {
    // Font Families
    fontFamily: {
      primary: '"Roboto", "Helvetica", "Arial", sans-serif',
      secondary: '"Poppins", "Roboto", sans-serif',
      monospace: '"Roboto Mono", "Monaco", "Consolas", monospace',
    },
    
    // Font Sizes
    fontSize: {
      xs: '0.75rem',      // 12px
      sm: '0.875rem',     // 14px
      base: '1rem',       // 16px
      lg: '1.125rem',     // 18px
      xl: '1.25rem',      // 20px
      '2xl': '1.5rem',    // 24px
      '3xl': '1.875rem',  // 30px
      '4xl': '2.25rem',   // 36px
      '5xl': '3rem',      // 48px
      '6xl': '3.75rem',   // 60px
    },
    
    // Font Weights
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    
    // Line Heights
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
    
    // Letter Spacing
    letterSpacing: {
      tight: '-0.025em',
      normal: '0em',
      wide: '0.025em',
      wider: '0.05em',
      widest: '0.1em',
    },
  },

  // ===== SPACING =====
  spacing: {
    // Base spacing unit (8px)
    xs: '0.25rem',        // 4px
    sm: '0.5rem',         // 8px
    md: '1rem',           // 16px
    lg: '1.5rem',         // 24px
    xl: '2rem',           // 32px
    '2xl': '3rem',        // 48px
    '3xl': '4rem',        // 64px
    '4xl': '6rem',        // 96px
    '5xl': '8rem',        // 128px
    
    // Component-specific spacing
    component: {
      padding: '1.5rem',      // 24px
      margin: '1rem',         // 16px
      gap: '1rem',            // 16px
      section: '3rem',        // 48px
    },
    
    // Layout spacing
    layout: {
      header: '4rem',         // 64px
      sidebar: '16rem',       // 256px
      content: '2rem',        // 32px
      footer: '3rem',         // 48px
    },
  },

  // ===== SIZES =====
  sizes: {
    // Component dimensions
    component: {
      button: {
        height: {
          small: '32px',
          medium: '40px',
          large: '48px',
        },
        minWidth: {
          small: '80px',
          medium: '100px',
          large: '120px',
        },
      },
      input: {
        height: '40px',
        minWidth: '200px',
      },
      card: {
        minHeight: '200px',
        maxWidth: '400px',
      },
      avatar: {
        small: '32px',
        medium: '40px',
        large: '56px',
        xlarge: '80px',
      },
    },
    
    // Layout dimensions
    layout: {
      maxWidth: '1200px',
      sidebarWidth: '240px',
      headerHeight: '64px',
      borderRadius: '8px',
    },
    
    // Icon sizes
    icon: {
      small: '16px',
      medium: '24px',
      large: '32px',
      xlarge: '48px',
    },
  },

  // ===== SHADOWS =====
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },

  // ===== BORDER RADIUS =====
  borderRadius: {
    none: '0',
    sm: '0.125rem',       // 2px
    md: '0.375rem',       // 6px
    lg: '0.5rem',         // 8px
    xl: '0.75rem',        // 12px
    '2xl': '1rem',        // 16px
    '3xl': '1.5rem',      // 24px
    full: '9999px',
  },

  // ===== TRANSITIONS =====
  transitions: {
    duration: {
      fast: '150ms',
      normal: '250ms',
      slow: '350ms',
    },
    easing: {
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },

  // ===== BREAKPOINTS =====
  breakpoints: {
    xs: '0px',
    sm: '600px',
    md: '900px',
    lg: '1200px',
    xl: '1536px',
  },

  // ===== Z-INDEX =====
  zIndex: {
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },

  // ===== COMPONENT-SPECIFIC STYLES =====
  components: {
    // Dashboard styles
    dashboard: {
      card: {
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#FFFFFF',
      },
      metric: {
        fontSize: '2rem',
        fontWeight: 600,
        color: '#212121',
      },
    },
    
    // Table styles
    table: {
      header: {
        backgroundColor: '#F5F5F5',
        fontWeight: 600,
      },
      row: {
        hover: {
          backgroundColor: '#F8F9FA',
        },
      },
    },
    
    // Form styles
    form: {
      field: {
        marginBottom: '1rem',
      },
      label: {
        fontWeight: 500,
        marginBottom: '0.5rem',
      },
    },
    
    // Button styles
    button: {
      primary: {
        backgroundColor: '#4A90E2',
        color: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#357ABD',
        },
      },
      secondary: {
        backgroundColor: '#F5A623',
        color: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#E0941A',
        },
      },
    },
  },
};

// ===== UTILITY FUNCTIONS =====

// Get color with opacity
export const getColorWithOpacity = (color, opacity) => {
  // Convert hex to rgba
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Get responsive spacing
export const getResponsiveSpacing = (spacing, breakpoint = 'md') => {
  const breakpoints = {
    xs: 0.5,
    sm: 0.75,
    md: 1,
    lg: 1.25,
    xl: 1.5,
  };
  
  const multiplier = breakpoints[breakpoint] || 1;
  return `calc(${spacing} * ${multiplier})`;
};

// Get component variant styles
export const getComponentVariant = (component, variant) => {
  return AppStyles.components[component]?.[variant] || {};
};

// Export individual sections for easier imports
export const { colors, typography, spacing, sizes, shadows, borderRadius, transitions, breakpoints, zIndex, components } = AppStyles;

export default AppStyles;
